import useSWR from 'swr';

import { Pack, Packs } from '@/components/app/cards/PacksList/types';
import { Tag, Tags } from '@/components/app/cards/TagsList/types';
import { useApi } from '@/hooks/api';
import { useUserState } from '@/hooks/store';

export interface PacksFeedResponse {
	feed: {
		id: string;
		title: string;
		tags: Tags;
		packs: Pack[];
		subtitle?: string;
	};
}

interface PacksFeedReturn {
	isLoading: boolean;
	packs: Packs;
	tags: Tag[];
	title?: string;
	subtitle?: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors the untyped `error` SWR already surfaces in useSearch
	error: any;
	hasResponse: boolean;
	notFound: boolean;
}

export function usePacksFeed(packFeedName?: string | null): PacksFeedReturn {
	const apiRequest = useApi();
	const user = useUserState();

	const { data, isValidating, error } = useSWR<PacksFeedResponse>(
		[packFeedName ? `packs/feeds/${packFeedName}` : null, packFeedName, user?.id],
		async ([endpoint]: [endpoint: string]) => {
			const res = await apiRequest<PacksFeedResponse>({
				endpoint,
				method: 'GET',
				customHeaders: {
					'x-device-capabilities': 'v1;signedCookie;streamOnly',
				},
			});
			return res.data;
		},
		{ revalidateOnFocus: false, errorRetryCount: 0 },
	);

	return {
		isLoading: isValidating,
		packs: data?.feed?.packs ?? [],
		tags: data?.feed?.tags ?? [],
		title: data?.feed?.title,
		subtitle: data?.feed?.subtitle,
		error,
		// Positive confirmation that a *usable* payload came back, which `error` alone does
		// not give: the shared axios interceptor rejects on an HTTP status >= 400 and on a
		// network-level failure (DNS, timeout, reset — no response at all), but a malformed
		// 200 (body missing `feed`) resolves with no error and nothing usable either.
		// Checking `data.feed` rather than just `data` means that case also counts as no
		// response, not as a confirmed-empty one — otherwise a caller would 404 a feed
		// that's really just behind a broken payload.
		hasResponse: data?.feed !== undefined,
		// A 404 is the server actively confirming this feed doesn't exist — the one error
		// shape that should override the "don't trust it" caution above and redirect anyway.
		// Every other error (5xx, or a network-level failure with no status at all) stays
		// unconfirmed, since neither means the feed itself doesn't exist.
		notFound: error?.status === 404,
	};
}
